import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaEye, FaBrain, FaCode, FaServer } from 'react-icons/fa';

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('cv');
  const [activeSkill, setActiveSkill] = useState(null);
  const containerRef = useRef(null);

  const skillTree = {
    cv: {
      title: 'Computer Vision',
      tagline: 'IMAGE & VIDEO INTELLIGENCE',
      icon: FaEye,
      skills: [
        { name: 'YOLOv8', level: 'Expert', desc: 'Custom network compilation, training on custom datasets, and high-FPS inferencing.' },
        { name: 'OpenCV', level: 'Advanced', desc: 'Real-time image manipulation, bounding boxes, perspective transforms, and filtering.' },
        { name: 'Object Tracking', level: 'Advanced', desc: 'Custom DeepSORT/ByteTrack setups for municipal flow, density estimation, and safety.' },
        { name: 'Segmentation', level: 'Intermediate', desc: 'Instance segmentation architectures for precise region-of-interest compliance.' },
      ],
      nodes: [
        { cx: 120, cy: 90, label: 'YOLOv8', id: 'yolo' },
        { cx: 280, cy: 70, label: 'OpenCV', id: 'opencv' },
        { cx: 180, cy: 220, label: 'Object Tracking', id: 'tracking' },
        { cx: 320, cy: 190, label: 'Segmentation', id: 'segmentation' },
      ],
      hub: { cx: 220, cy: 140, label: 'CV Engine' }
    },
    ml: {
      title: 'Machine Learning',
      tagline: 'PREDICTIVE ARCHITECTURES',
      icon: FaBrain,
      skills: [
        { name: 'PyTorch', level: 'Advanced', desc: 'Tensor modeling, neural network fine-tuning, and weights conversion.' },
        { name: 'TensorFlow', level: 'Advanced', desc: 'Model compiling, validation matrices, and CNN training pipelines.' },
        { name: 'Scikit-learn', level: 'Expert', desc: 'Data cleaning, feature scaling, classification trees, and linear modeling.' },
        { name: 'Deep Learning', level: 'Advanced', desc: 'Customizing multi-layer CNNs and tuning hyperparameters for inference speed.' },
      ],
      nodes: [
        { cx: 130, cy: 80, label: 'PyTorch', id: 'pytorch' },
        { cx: 290, cy: 90, label: 'TensorFlow', id: 'tensorflow' },
        { cx: 160, cy: 210, label: 'Scikit-learn', id: 'sklearn' },
        { cx: 310, cy: 200, label: 'Deep Learning', id: 'dl' },
      ],
      hub: { cx: 220, cy: 140, label: 'ML Model' }
    },
    web: {
      title: 'Web Dev & APIs',
      tagline: 'INTERFACE & BACKEND LAYERS',
      icon: FaCode,
      skills: [
        { name: 'React', level: 'Advanced', desc: 'State architecture, performance rendering, Framer Motion, and GSAP.' },
        { name: 'FastAPI', level: 'Expert', desc: 'Asynchronous endpoint management, Pydantic validation, and server speed tuning.' },
        { name: 'Node.js', level: 'Advanced', desc: 'REST server frameworks, async request cycles, and package scripting.' },
        { name: 'REST APIs', level: 'Expert', desc: 'Creating robust models and JSON structures for model inference integration.' },
      ],
      nodes: [
        { cx: 110, cy: 100, label: 'React', id: 'react' },
        { cx: 270, cy: 60, label: 'FastAPI', id: 'fastapi' },
        { cx: 190, cy: 230, label: 'Node.js', id: 'node' },
        { cx: 330, cy: 170, label: 'REST APIs', id: 'rest' },
      ],
      hub: { cx: 220, cy: 140, label: 'Web Stack' }
    },
    devops: {
      title: 'DevOps & Scaling',
      tagline: 'CONTAINERIZATION & DEPLOYMENT',
      icon: FaServer,
      skills: [
        { name: 'Docker', level: 'Advanced', desc: 'Containerizing Python environments, setting up volumes, and compiling lightweight images.' },
        { name: 'AWS Cloud', level: 'Advanced', desc: 'Deploying servers on EC2, managing S3 buckets, and configuring security rules.' },
        { name: 'Git Protocol', level: 'Expert', desc: 'Branch workflows, conflict resolution, version tags, and CI/CD pipelines.' },
        { name: 'PostgreSQL', level: 'Intermediate', desc: 'Relational database schema optimization and executing high-speed indexing queries.' },
      ],
      nodes: [
        { cx: 140, cy: 70, label: 'Docker', id: 'docker' },
        { cx: 300, cy: 100, label: 'AWS Cloud', id: 'aws' },
        { cx: 160, cy: 220, label: 'Git Protocol', id: 'git' },
        { cx: 320, cy: 180, label: 'PostgreSQL', id: 'postgres' },
      ],
      hub: { cx: 220, cy: 140, label: 'Infra Stack' }
    }
  };

  const secondaryTools = [
    'Pandas', 'NumPy', 'Flask', 'Django', 'SQLAlchemy', 'Pydantic', 'Matplotlib', 
    'Docker Compose', 'GitHub Actions', 'Jupyter', 'Linux OS', 'Shell Scripting', 
    'Nginx', 'C++', 'Python OOP', 'Tailwind CSS', 'Vite'
  ];

  const currentCategoryData = skillTree[activeCategory];

  const handleNodeClick = (nodeLabel) => {
    const matched = currentCategoryData.skills.find(s => s.name.toLowerCase() === nodeLabel.toLowerCase());
    if (matched) {
      setActiveSkill(matched);
    }
  };

  useEffect(() => {
    if (currentCategoryData && currentCategoryData.skills.length > 0) {
      setActiveSkill(currentCategoryData.skills[0]);
    }
  }, [activeCategory]);

  return (
    <section
      id="skills"
      className="section-padding relative overflow-hidden bg-void border-t border-grey-800"
    >
      <div className="container-custom relative z-10">
        
        {/* Title */}
        <div className="mb-20 text-left border-l-2 border-white-primary pl-6">
          <span className="inline-block text-[9px] font-mono font-bold tracking-[0.35em] uppercase text-grey-400 mb-2">
            02 // TECHNOLOGY_TREE
          </span>
          <h2 className="text-3xl sm:text-5xl md:text-6xl font-display font-black">
            SKILL // <span className="chrome-text">MAPPING</span>
          </h2>
        </div>

        {/* HUD Category Navigation */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-10">
          {Object.entries(skillTree).map(([key, cat]) => {
            const IconComponent = cat.icon;
            const isActive = activeCategory === key;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`p-4 border text-left rounded-none font-mono transition-all duration-200 ${
                  isActive
                    ? 'bg-white-primary text-void border-white-pure font-bold'
                    : 'bg-elevated border-grey-800 text-grey-400 hover:text-white-pure hover:border-grey-600'
                }`}
              >
                <div className="text-[8px] tracking-widest uppercase text-grey-400 font-semibold mb-1">
                  SYSTEM // {key.toUpperCase()}
                </div>
                <div className="text-xs font-bold flex items-center gap-2">
                  <IconComponent className={`text-xs ${isActive ? 'text-void' : 'text-grey-400'}`} />
                  <span>{cat.title}</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Tree Container & Info Card */}
        <div ref={containerRef} className="grid lg:grid-cols-12 gap-8 items-stretch">
          
          {/* Interactive SVG Node Map */}
          <div className="lg:col-span-7 bg-elevated border border-grey-800 p-6 flex flex-col justify-between items-center relative min-h-[380px] overflow-hidden">
            <div className="absolute top-4 left-4 text-[9px] font-mono text-grey-600 tracking-wider">
              VIEWPORT // INTERACTIVE_MAP
            </div>
            
            <svg 
              className="w-full max-w-[480px] h-[300px] mt-6"
              viewBox="0 0 440 300"
            >
              {/* Lines from Category Hub to Nodes */}
              {currentCategoryData.nodes.map((node, i) => (
                <g key={i}>
                  <motion.line
                    x1={currentCategoryData.hub.cx}
                    y1={currentCategoryData.hub.cy}
                    x2={node.cx}
                    y2={node.cy}
                    stroke="#1f1f1f"
                    strokeWidth="1.5"
                  />
                  <motion.line
                    x1={currentCategoryData.hub.cx}
                    y1={currentCategoryData.hub.cy}
                    x2={node.cx}
                    y2={node.cy}
                    stroke="#ffffff"
                    strokeWidth="1"
                    className="opacity-40"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ repeat: Infinity, duration: 3, delay: i * 0.4 }}
                  />
                </g>
              ))}

              {/* Category Center Hub */}
              <circle
                cx={currentCategoryData.hub.cx}
                cy={currentCategoryData.hub.cy}
                r="18"
                className="fill-void stroke-white-pure stroke-[2]"
              />
              <text
                x={currentCategoryData.hub.cx}
                y={currentCategoryData.hub.cy + 4}
                textAnchor="middle"
                className="fill-white-pure font-mono text-[8px] font-bold pointer-events-none"
              >
                HUB
              </text>

              {/* Skill Nodes */}
              {currentCategoryData.nodes.map((node, i) => {
                const isActive = activeSkill?.name.toLowerCase() === node.label.toLowerCase();
                return (
                  <g 
                    key={i} 
                    className="cursor-pointer group"
                    onClick={() => handleNodeClick(node.label)}
                  >
                    <motion.circle
                      cx={node.cx}
                      cy={node.cy}
                      r="12"
                      className={`transition-all duration-300 ${
                        isActive 
                          ? 'fill-white-primary stroke-white-pure stroke-[2]' 
                          : 'fill-void stroke-grey-600 stroke-[1.5] group-hover:stroke-white-pure'
                      }`}
                    />
                    
                    <text
                      x={node.cx}
                      y={node.cy - 18}
                      textAnchor="middle"
                      className={`font-mono text-[10px] font-bold transition-colors duration-300 ${
                        isActive ? 'fill-white-pure' : 'fill-grey-400 group-hover:fill-white-pure'
                      }`}
                    >
                      {node.label}
                    </text>
                  </g>
                );
              })}
            </svg>

            <div className="w-full text-center text-[10px] font-mono text-grey-600 tracking-widest mt-4">
              TAP OR CLICK NODES TO INSPECT ENGINE SPECIFICATIONS
            </div>
          </div>

          {/* Node Specification Readout Card */}
          <div className="lg:col-span-5 bg-elevated border border-grey-800 p-8 flex flex-col justify-between relative">
            <div className="absolute top-4 left-4 text-[9px] font-mono text-grey-600 tracking-wider">
              TELEMETRY // SPEC_READOUT
            </div>
            
            <AnimatePresence mode="wait">
              {activeSkill ? (
                <motion.div
                  key={activeSkill.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6 pt-4 text-left"
                >
                  <div>
                    <span className="px-2.5 py-1 border border-grey-800 text-[8px] font-mono text-grey-400 font-bold uppercase">
                      LEVEL: {activeSkill.level}
                    </span>
                    <h3 className="text-2xl font-display font-black text-white-pure mt-4 uppercase">
                      {activeSkill.name}
                    </h3>
                  </div>

                  <div className="border-t border-grey-800 pt-6">
                    <h4 className="text-[10px] font-mono text-grey-600 uppercase font-semibold mb-2">FUNCTIONAL_DESCRIPTION:</h4>
                    <p className="text-sm text-grey-200 leading-relaxed font-sans">
                      {activeSkill.desc}
                    </p>
                  </div>

                  <div className="border-t border-grey-800 pt-6">
                    <div className="flex justify-between items-center text-xs font-mono text-grey-400">
                      <span>VERIFICATION_STATUS:</span>
                      <span className="text-white-pure font-bold">VERIFIED_OK</span>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="h-full flex items-center justify-center text-xs font-mono text-grey-600 uppercase">
                  No active telemetry
                </div>
              )}
            </AnimatePresence>

            <div className="text-[9px] font-mono text-grey-600 tracking-wider mt-8 border-t border-grey-800 pt-4 text-left">
              NODE // CAT_REF: {currentCategoryData.tagline}
            </div>
          </div>

        </div>

        {/* Secondary Skills Scrolling Marquee */}
        <div className="mt-16 border border-grey-800 bg-elevated/40 py-5 overflow-hidden relative select-none">
          <div className="absolute top-0 bottom-0 left-0 w-20 bg-gradient-to-r from-void to-transparent z-10" />
          <div className="absolute top-0 bottom-0 right-0 w-20 bg-gradient-to-l from-void to-transparent z-10" />
          
          <div className="flex animate-marquee whitespace-nowrap gap-8 items-center">
            {[...secondaryTools, ...secondaryTools].map((tool, idx) => (
              <div 
                key={idx}
                className="flex items-center gap-4 text-xs font-mono tracking-widest text-grey-400 uppercase"
              >
                <span>{tool}</span>
                <span className="text-grey-800">|</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Skills;
